// `/enroll` — spec 15 §3.1.3 and §4 screen S2: mandatory TOTP enrolment, then
// the recovery codes, shown exactly once.
//
// ONE SCREEN, TWO PHASES, and the phase is driven by what the server has
// already done, never by a guess:
//
//   setup  — POST auth/totp/enroll has minted a secret. The `otpauth://` URI is
//            drawn as a QR **in this browser** and the secret is offered as
//            selectable text for manual entry. The 6-digit field confirms it.
//   saved  — POST auth/totp/confirm answered with the ten recovery codes.
//            THESE ten exist nowhere else, ever again: the server keeps only
//            their hashes and no endpoint returns them a second time. The one
//            recovery from having lost them is `/console`'s reissue, which
//            mints a DIFFERENT batch and voids these — the presentation is
//            shared (components/recovery-codes.tsx) precisely so the two places
//            codes can appear cannot drift apart in wording or in gating.
//
// WHY THE ENROL CALL IS FIRED EXACTLY ONCE. Every call to
// `POST auth/totp/enroll` generates a FRESH secret and overwrites the stored
// one (staff-auth.service.ts §3.1.3 step 1) — so a second call silently
// invalidates a QR the user may already have scanned, and their app would then
// produce codes the server rejects forever. The call is therefore fired behind
// a ref guard (StrictMode double-mounts effects in dev), and the only way to
// re-run it is the explicit retry, which says in as many words that the
// previous code is dead and must be re-scanned.
//
// WHAT NEVER LEAVES THIS COMPONENT: the secret, the otpauth URI and the ten
// recovery codes live in React state and in nothing else. They are not written
// to localStorage or sessionStorage, not put in the URL, not logged, and not
// sent anywhere except the two endpoints above. The QR is encoded locally
// (lib/qr.ts) precisely so the secret never reaches an image service.

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { AdminAuthCard } from "../admin-shell";
import { QrCode } from "../components/qr-code";
import { RecoveryCodes } from "../components/recovery-codes";
import { useForgetPlatformSession, useMarkTotpConfirmed } from "../hooks/use-platform-session";
import { setAdminFlash } from "../lib/flash";
import {
  classifyTotpCheckFailure,
  confirmTotp,
  enrollTotp,
  type TotpCheckFailure,
  type TotpEnrollment,
} from "../lib/platform-api";

type Phase = { kind: "setup" } | { kind: "saved"; codes: readonly string[] };

/**
 * The sentence a failed confirm earns — keyed off WHAT THE FAILURE PROVES, not
 * off the fact that something failed (probed 2026-08-17, the unknown-bad-broken
 * audit). Every failure used to collapse into "That code was not accepted" —
 * including a TypeError('Failed to fetch') that never reached the server. On
 * THIS screen that lie is destructive: the header above records that a second
 * enrol call mints a fresh secret and permanently kills the QR already scanned,
 * so blaming the code for a network blip pushes the operator to abandon a
 * working enrolment and press the one retry that destroys it.
 *
 *   rejected / shape — the collapsed no-oracle sentence, unchanged: the server
 *                      (or the contract) judged the INPUT, and wrong, stale and
 *                      replayed must keep reading identically so the banner
 *                      never describes the TOTP window to a guesser.
 *   broken           — transport / 5xx: nothing judged the code, and the honest
 *                      instruction is "same code or the next one" — the secret
 *                      on both sides is untouched.
 *   session_expired  — rendered for the frame before the onError below lands on
 *                      /login, and left standing if that navigation ever fails:
 *                      the screen must never be wordless about a dead session.
 */
function confirmErrorKey(failure: TotpCheckFailure): string {
  switch (failure.kind) {
    case "rejected":
    case "shape":
      return "admin.enroll.failed";
    case "session_expired":
      return "admin.enroll.sessionExpired";
    case "broken":
      return "admin.enroll.notChecked";
  }
}

export function AdminEnrollScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const markTotpConfirmed = useMarkTotpConfirmed();
  const forgetSession = useForgetPlatformSession();

  const [phase, setPhase] = useState<Phase>({ kind: "setup" });
  const [enrollment, setEnrollment] = useState<TotpEnrollment | undefined>(undefined);
  const [code, setCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  const enroll = useMutation({
    mutationFn: enrollTotp,
    onSuccess: setEnrollment,
  });

  // Fire once. `enroll.mutate` is stable for the life of the component and the
  // ref makes the effect idempotent under StrictMode's double-invoke, so this
  // runs exactly one enrolment per mount — see the header note on why a second
  // call would be actively harmful.
  const started = useRef(false);
  const start = enroll.mutate;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  const confirm = useMutation({
    mutationFn: () => confirmTotp(code.trim()),
    onError: (error: unknown) => {
      // A 401 with no TOTP verdict means the restricted session died before
      // anything looked at the code — retrying from this screen can only 401
      // again, so leaving the form standing would be a different lie ("try
      // again" where trying again cannot work). Send them to the one door that
      // does, with the reason waiting on it (the same flash hand-off
      // accept-invite uses), and drop the cached identity the way sign-out
      // does so nothing renders a session the server has already ended.
      // Signing back in lands a totp_pending staff member straight back here
      // with a fresh QR, so the flow re-guides them without further prose.
      if (classifyTotpCheckFailure(error).kind !== "session_expired") return;
      forgetSession();
      setAdminFlash("session_expired");
      void navigate({ to: "/login" });
    },
    onSuccess: (codes) => {
      setCode("");
      // NOTE WHAT IS *NOT* DONE HERE. The server is now `active`, and the
      // cached `/me` still says `totp_pending` — but priming it at this instant
      // evicts the user off the screen that is about to render. THIS ROUTE's
      // own guard is `requires="pending"`; flip the cache and it computes
      // "wrong phase" and redirects to /console, taking the ten recovery codes
      // with it before anyone has read them. (Measured, not reasoned: the
      // walkthrough landed on the account screen with the codes never shown.)
      // The cache is primed instead at the moment the user LEAVES — see the
      // Continue handler below.
      setPhase({ kind: "saved", codes });
    },
  });

  if (phase.kind === "saved") {
    return (
      <AdminAuthCard
        title={t("admin.recovery.title")}
        data-testid="admin-recovery"
        className="admin-card-wide"
      >
        <RecoveryCodes
          codes={phase.codes}
          doneLabel={t("admin.recovery.continue")}
          onDone={() => {
            // THE CACHE IS PRIMED HERE, one instruction before the navigation
            // that depends on it. `/console`'s guard branches on the cached
            // `/me`, whose `staleTime` is 30 s — so without this the guard read
            // `totp_confirmed: false` for up to half a minute after the server
            // had already flipped it, bounced back to `/enroll`, and re-fired
            // an enrolment the API refuses with 409 STAFF_NOT_TOTP_PENDING.
            // What the user saw was "Could not start two-factor setup", on the
            // one button this screen tells them to press, with the ten recovery
            // codes unmounted behind it. Priming rather than invalidating,
            // because the 200 that produced these codes IS the server saying
            // the phase moved — there is nothing to go and ask.
            markTotpConfirmed();
            void navigate({ to: "/console" });
          }}
        />
      </AdminAuthCard>
    );
  }

  // ---- loading -------------------------------------------------------------
  if (enroll.isPending || (enrollment === undefined && enroll.error === null)) {
    return (
      <AdminAuthCard title={t("admin.enroll.title")} data-testid="admin-enroll">
        <div className="admin-state" data-testid="admin-enroll-loading">
          <p>{t("admin.enroll.loading")}</p>
        </div>
      </AdminAuthCard>
    );
  }

  // ---- error ---------------------------------------------------------------
  if (enrollment === undefined) {
    return (
      <AdminAuthCard title={t("admin.enroll.errorTitle")} data-testid="admin-enroll">
        <Banner kind="bad" data-testid="admin-enroll-error">
          {t("admin.enroll.errorBody")}
        </Banner>
        <div className="admin-actions">
          <Button
            variant="primary"
            busy={enroll.isPending}
            onClick={() => {
              enroll.mutate();
            }}
            data-testid="admin-enroll-retry"
          >
            {t("admin.enroll.retry")}
          </Button>
        </div>
      </AdminAuthCard>
    );
  }

  // Within the VERDICT class every refusal still reads the same — a wrong code,
  // a stale code and a replayed code are all "that code was not accepted",
  // because telling them apart would describe the server's TOTP window to
  // whoever is guessing. But the class boundary is drawn first: a failure that
  // carries no verdict (classifyTotpCheckFailure) must never wear that
  // sentence — see confirmErrorKey above for the incident.
  const confirmFailure =
    confirm.error === null ? undefined : classifyTotpCheckFailure(confirm.error);

  return (
    <AdminAuthCard
      title={t("admin.enroll.title")}
      lead={t("admin.enroll.lead")}
      data-testid="admin-enroll"
    >
      <div className="admin-enroll">
        <QrCode
          value={enrollment.otpauth_uri}
          title={t("admin.enroll.qrAlt")}
          data-testid="admin-enroll-qr"
          fallback={
            <Banner kind="neutral" data-testid="admin-enroll-qr-unavailable">
              {t("admin.enroll.qrUnavailable")}
            </Banner>
          }
        />
        <div className="admin-secret">
          <span className="admin-secret-lb" id="admin-secret-lb">
            {t("admin.enroll.secretLabel")}
          </span>
          {/* Selectable, never a form control: nothing can submit it anywhere. */}
          <code className="admin-secret-v" data-testid="admin-enroll-secret">
            {enrollment.secret}
          </code>
          <div className="admin-actions">
            <Button
              size="sm"
              onClick={() => {
                // Same widened-type guard as the recovery codes: an insecure
                // context genuinely has no clipboard, and the secret is
                // selectable text either way.
                const { clipboard } = navigator as unknown as { clipboard?: Clipboard };
                if (clipboard === undefined) {
                  setSecretCopied(false);
                  return;
                }
                void clipboard.writeText(enrollment.secret).then(
                  () => {
                    setSecretCopied(true);
                  },
                  () => {
                    setSecretCopied(false);
                  },
                );
              }}
              data-testid="admin-enroll-copy-secret"
            >
              {secretCopied ? t("admin.enroll.copied") : t("admin.enroll.copySecret")}
            </Button>
          </div>
          <p className="admin-hint">{t("admin.enroll.secretHelp")}</p>
          <p className="admin-hint">{t("admin.enroll.privacy")}</p>
        </div>
      </div>

      {confirmFailure !== undefined ? (
        <Banner kind="bad" data-testid="admin-enroll-code-error">
          {t(confirmErrorKey(confirmFailure))}
          {/* The request id rides along when the server answered with one (a
              5xx carries it; a dead socket has none) — same presentation as
              accept-invite's, so a support thread can quote the exact failed
              request instead of "it said try again". */}
          {confirmFailure.kind === "broken" && confirmFailure.requestId !== undefined ? (
            <span className="admin-reqid">
              {" "}
              {t("admin.enroll.requestId")}: <code>{confirmFailure.requestId}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}

      <form
        className="admin-form"
        data-testid="admin-enroll-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim().length === 0) return;
          confirm.mutate();
        }}
      >
        <div className="ds-field">
          <label htmlFor="admin-enroll-code">{t("admin.enroll.code")}</label>
          <input
            id="admin-enroll-code"
            className="ds-input admin-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            // Focused for the same reason the login TOTP field is: the user is
            // holding a phone showing six digits that expire in under thirty
            // seconds, and a click to find the box is time they do not have.
            autoFocus
            spellCheck={false}
            maxLength={6}
            value={code}
            data-testid="admin-enroll-code"
            onChange={(e) => {
              setCode(e.target.value);
            }}
            required
          />
        </div>
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={confirm.isPending}
            disabled={code.trim().length === 0}
            data-testid="admin-enroll-submit"
          >
            {confirm.isPending ? t("admin.enroll.confirming") : t("admin.enroll.confirm")}
          </Button>
        </div>
      </form>
    </AdminAuthCard>
  );
}
